import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, from, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, groupBy, map, mergeMap, shareReplay, switchMap, throttleTime } from 'rxjs/operators';
import { FeatureFlagService } from '../root/feature-flags.service';
import { RouterStateService } from '../root/router-state.service';
import {
  CompanyChargesDetails,
  SettlementStatement,
  SettlementStatementPaymentIssueType,
  StatementsOverview,
} from '../types';
import { STATEMENT_ID } from './settlement-statement-routing.constants';

@Injectable({
  providedIn: 'root',
})
export class StatementService {
  private summaries$$ = new BehaviorSubject<StatementsOverview>(null);
  private summariesThrottle$$ = new Subject();
  private summariesShared$ = this.summaries$$.pipe(shareReplay(1));

  public get summaries$(): Observable<StatementsOverview> {
    this.summariesThrottle$$.next();
    return this.summariesShared$;
  }

  private currentStatement$$ = new BehaviorSubject<SettlementStatement>(null);
  private currentStatementShared$ = this.currentStatement$$.pipe(shareReplay(1));

  public get currentStatement$(): Observable<SettlementStatement> {
    return this.currentStatementShared$;
  }

  private statements$$ = new BehaviorSubject<Record<string, SettlementStatement>>({});
  private statementsThrottle$$ = new Subject<number>();
  private statementsShared$ = this.statements$$.pipe(shareReplay(1));

  private companyCharges$$ = new BehaviorSubject<CompanyChargesDetails>(null);
  private companyChargesShared$ = this.companyCharges$$.pipe(shareReplay(1));
  private companyChargesThrottle$$ = new Subject<number>();

  public get companyCharges$(): Observable<CompanyChargesDetails> {
    this.companyChargesThrottle$$.next();
    return this.companyChargesShared$;
  }

  public getStatement$(id: number): Observable<SettlementStatement> {
    this.statementsThrottle$$.next(id);
    return this.statementsShared$.pipe(
      map((statements) => {
        if (statements[id]) {
          return statements[id];
        }
        return null;
      }),
    );
  }

  constructor(
    private httpClient: HttpClient,
    private logger: NGXLogger,
    private routerState: RouterStateService,
    private featureFlag: FeatureFlagService,
  ) {
    this.summariesThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadSummaries();
    });

    this.companyChargesThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadCharges();
    });

    this.statementsThrottle$$
      .pipe(
        groupBy((value) => value),
        mergeMap((grouped$) => grouped$.pipe(throttleTime(1000))),
        switchMap((id) => this.loadStatement$(id)),
      )
      .subscribe((statement) => {
        const current = this.statements$$.value;
        this.statements$$.next({
          ...current,
          [statement.statement.id]: statement,
        });
      });

    this.listenForStatement();
  }

  private async listenForStatement() {
    this.routerState
      .listenForParamChange$(STATEMENT_ID)
      .pipe(
        distinctUntilChanged(),
        switchMap((statementId) => {
          if (statementId) {
            return this.loadStatement$(+statementId);
          }
          return of(null);
        }),
      )
      .subscribe((statement: SettlementStatement) => {
        this.currentStatement$$.next(statement);
      });
  }

  private async loadSummaries() {
    const v2Statements = await this.featureFlag.isFlagActiveNow('lohiSettlementStatements');
    const prefix = v2Statements ? 'v2' : 'v1';
    try {
      const result = await this.httpClient
        .get<StatementsOverview>(`${environment.api}/${prefix}/admin/settlement_statement`)
        .toPromise();
      this.summaries$$.next(result || ({} as StatementsOverview));
    } catch (error) {
      this.logger.error(error);
    }
  }

  public refreshSummaries(): void {
    this.summariesThrottle$$.next();
  }

  private async loadCharges() {
    try {
      const result = await this.httpClient
        .get<CompanyChargesDetails>(`${environment.api}/v1/admin/company_charges`)
        .toPromise();
      this.companyCharges$$.next(result);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private loadStatement$(id: number): Observable<SettlementStatement> {
    return from(this.featureFlag.isFlagActiveNow('lohiSettlementStatements')).pipe(
      switchMap((v2Statements) => {
        const prefix = v2Statements ? 'v2' : 'v1';
        return this.httpClient
          .get<SettlementStatement>(`${environment.api}/${prefix}/admin/settlement_statement/${id}`)
          .pipe(map((response) => response));
      }),
    );
  }

  public getTransactions$() {
    return this.httpClient.get<string>(`${environment.api}/v1/admin/fuel_transactions/export`, {
      headers: {
        Accept: 'text/csv',
        'Content-Type': 'text/csv',
      },
      responseType: 'blob' as any,
    });
  }

  public getIssueTypes$(): Observable<SettlementStatementPaymentIssueType[]> {
    const url = `${environment.api}/v2/admin/settlement_statement/issue_types`;
    return this.httpClient
      .get<{ types: SettlementStatementPaymentIssueType[] }>(url)
      .pipe(map((response) => response.types || []));
  }

  public reportPaymentIssue$(statementId: number, issueTypeIds: string[]) {
    const url = `${environment.api}/v2/admin/settlement_statement/${statementId}/payment_issue`;
    return this.httpClient.post(url, { issueTypeIds });
  }
}
